import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import axios from 'axios';
import { applyConsent } from '../../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CookieConsentBanner = () => {
  const { t } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState({
    functional: true, // Always required
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already consented
    const hasConsent = localStorage.getItem('wedvaya_cookie_consent');
    if (!hasConsent) {
      // Delay showing banner for better UX
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
    try {
      applyConsent(JSON.parse(hasConsent));
    } catch (error) {
      // Ignore malformed stored consent
    }
  }, []);

  const saveConsent = async (forcedConsent = null) => {
    const finalConsent = forcedConsent || consent;

    try {
      await axios.post(`${API_URL}/api/consent/cookies`, finalConsent);
    } catch (error) {
      console.error('Failed to save consent:', error);
    }

    localStorage.setItem('wedvaya_cookie_consent', JSON.stringify(finalConsent));
    setShowBanner(false);

    applyConsent(finalConsent);
  };

  const handleRejectAll = () => {
    saveConsent({ functional: true, analytics: false, marketing: false });
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom duration-500">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-border overflow-hidden">
        {!showSettings ? (
          // Simple Banner
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Cookie className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-1">
                  {t('cookies.title') || 'We use cookies'}
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {t('cookies.description') || 'We use cookies to improve your experience, analyze traffic, and for marketing purposes. By clicking "Accept All", you consent to our use of cookies.'}
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => saveConsent({ functional: true, analytics: true, marketing: true })} className="rounded-full">
                    <Check className="h-4 w-4 mr-2" />
                    {t('cookies.acceptAll') || 'Accept All'}
                  </Button>
                  <Button variant="outline" onClick={handleRejectAll} className="rounded-full">
                    {t('cookies.rejectAll') || 'Reject All'}
                  </Button>
                  <Button variant="ghost" onClick={() => setShowSettings(true)} className="rounded-full">
                    <Settings className="h-4 w-4 mr-2" />
                    {t('cookies.customize') || 'Customize'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Settings Panel
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-lg">
                {t('cookies.settingsTitle') || 'Cookie Settings'}
              </h3>
              <button onClick={() => setShowSettings(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {/* Functional Cookies - Always enabled */}
              <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                <div>
                  <p className="font-medium">{t('cookies.functional') || 'Functional Cookies'}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('cookies.functionalDesc') || 'Essential for the website to function properly.'}
                  </p>
                </div>
                <Switch checked={true} disabled className="opacity-50" />
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                <div>
                  <p className="font-medium">{t('cookies.analytics') || 'Analytics Cookies'}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('cookies.analyticsDesc') || 'Help us understand how visitors interact with the website.'}
                  </p>
                </div>
                <Switch 
                  checked={consent.analytics} 
                  onCheckedChange={(checked) => setConsent(prev => ({ ...prev, analytics: checked }))}
                />
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                <div>
                  <p className="font-medium">{t('cookies.marketing') || 'Marketing Cookies'}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('cookies.marketingDesc') || 'Used to deliver personalized advertisements.'}
                  </p>
                </div>
                <Switch 
                  checked={consent.marketing} 
                  onCheckedChange={(checked) => setConsent(prev => ({ ...prev, marketing: checked }))}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={() => saveConsent()} className="flex-1 rounded-full">
                {t('cookies.saveSettings') || 'Save Settings'}
              </Button>
              <Button variant="outline" onClick={() => saveConsent({ functional: true, analytics: true, marketing: true })} className="flex-1 rounded-full">
                {t('cookies.acceptAll') || 'Accept All'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;
